/**
 * @file OrderReviewRequestDialog.tsx
 * @description Запрос отзыва о заказе — подтверждение и результат
 * (phase19.10 R12-R14; переехал из блока карточки в phase19.24 шаг 5).
 *
 * Отдельной секции в карточке заказа больше нет: письмо клиенту — редкое
 * действие, и постоянный блок занимал экран у каждого доставленного заказа.
 * Теперь это пункт «⋯» и кнопка в «Что дальше», а условия отправки (заказ
 * доставлен, не больше двух писем, пауза, отписка) показываются здесь — перед
 * отправкой, а не после отказа.
 *
 * Все ограничения считает бэкенд, диалог только показывает его вердикт: свои
 * правила клиент дублировать не имеет права — разъедутся.
 */

import { Loader2, Send } from "lucide-react";
import { useEffect, useState } from "react";

import { Alert } from "../ui/alert";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { useNotify } from "../ui/NotifyProvider";
import { ApiError } from "../../lib/api-client";
import {
  reviewRequests,
  type ReviewRequestAvailability,
} from "../../lib/api-product-reviews";
import { formatDocDateTime } from "../wms/wms-document-meta";

interface Props {
  orderId: string;
  open: boolean;
  onClose: () => void;
}

export function OrderReviewRequestDialog({ orderId, open, onClose }: Props) {
  const notify = useNotify();
  const [state, setState] = useState<ReviewRequestAvailability | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);

  // Состояние перечитываем на каждое открытие: письмо мог отправить коллега.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    reviewRequests
      .availability(orderId)
      .then((data) => {
        if (!cancelled) setState(data);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState(null);
        setError(
          err instanceof ApiError ? err.message : "Не удалось узнать, можно ли просить отзыв",
        );
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, orderId]);

  async function onSend(): Promise<void> {
    setSending(true);
    try {
      setState(await reviewRequests.send(orderId));
      notify.success("Запрос отзыва отправлен клиенту");
      onClose();
    } catch (err) {
      notify.error(err instanceof ApiError ? err.message : "Не удалось отправить запрос");
    } finally {
      setSending(false);
    }
  }

  const lastSent = state?.history[0];
  const label = state && state.attemptsUsed > 0 ? "Напомнить об отзыве" : "Запросить отзыв";

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      {/* На телефоне диалог во весь экран — правило mobile-first DS. */}
      <DialogContent className="max-w-md" fullScreen>
        <DialogHeader>
          <DialogTitle>Отзыв о заказе</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          {loading ? (
            <div className="flex h-20 items-center justify-center">
              <Loader2 className="size-5 animate-spin text-on-surface-variant" />
            </div>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : state ? (
            <>
              <p className="text-sm text-on-surface-variant">
                {state.canSend
                  ? "Клиент получит письмо с просьбой оценить купленные товары и одноразовой ссылкой в личный кабинет."
                  : (state.reason ?? "Сейчас просить отзыв нельзя")}
              </p>
              <p className="text-xs text-on-surface-variant">
                Отправлено {state.attemptsUsed} из {state.maxAttempts}
                {lastSent ? ` · последнее ${formatDocDateTime(lastSent.sentAt)}` : ""}
                {lastSent?.sentBy ? ` · ${lastSent.sentBy}` : ""}
                {lastSent?.convertedAt ? " · отзыв получен" : ""}
              </p>
            </>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="outline" className="min-h-12 sm:min-h-10" onClick={onClose}>
            Закрыть
          </Button>
          <Button
            className="min-h-12 sm:min-h-10"
            onClick={() => void onSend()}
            disabled={!state?.canSend || sending}
          >
            {sending ? (
              <Loader2 className="size-4 animate-spin" aria-hidden />
            ) : (
              <Send className="size-4" aria-hidden />
            )}
            {label}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
